import { useState } from "react";
import DataTable from "@/components/organisms/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import { Account } from "@/types/Account";
import ColumnHeader from "@/components/atoms/ColumnHeader";
import PageHeader from "@/components/molecules/PageHeader";
import Button from "@/components/atoms/Button";
import { ArrowUpDown, Plus } from "lucide-react";
import Link from "@/components/atoms/Link";
import Badge from "@/components/atoms/Badge";
import { capitalize } from "@/lib/utils";
import SearchBox from "@/components/molecules/SearchBox";
import Container from "@/components/atoms/Container";
import useAccess from "@/hooks/useAccess";
import * as Constants from "@/constants";
import Pagination from "@/components/atoms/Pagination";
import { router } from "@inertiajs/react";

type Props = {
  accounts: Account[];
};

const columns: ColumnDef<Account>[] = [
  {
    accessorKey: "id",
    header: ({ column }) => <ColumnHeader column={column}>ID</ColumnHeader>,
  },
  {
    accessorKey: "name",
    header: ({ column }) => <ColumnHeader column={column}>Name</ColumnHeader>,
    cell: ({ row }) => (
      <div className="flex items-center space-x-2">
        <img
          src={row.original.avatar_thumb || "/default_avatar.png"}
          alt={row.original.name}
          className="w-8 h-8 rounded-full dark:bg-darkTextColor"
        />
        <span>{row.original.name}</span>
      </div>
    ),
  },
  {
    accessorKey: "initials",
    header: ({ column }) => (
      <ColumnHeader column={column}>Initials</ColumnHeader>
    ),
  },
  {
    accessorKey: "status",
    header: ({ column }) => <ColumnHeader column={column}>Status</ColumnHeader>,
    cell: ({ row }) => (
      <div className="space-x-2">
        <Badge
          variant={
            row.original.status === Constants.ACCOUNT_STATUSES.ACTIVE
              ? "success"
              : "destructive"
          }
          className="p-1"
        />
        <span>{capitalize(row.original.status.toLowerCase())}</span>
      </div>
    ),
  },
  {
    accessorKey: "currency",
    header: ({ column }) => (
      <ColumnHeader column={column}>Currency</ColumnHeader>
    ),
  },
];

const Index = ({ accounts }: Props) => {
  const [pagination, setPagination] = useState({ page: 1, per_page: 10 });
  const [totalCount, setTotalCount] = useState<number>(accounts.length);
  const [totalPages, setTotalPages] = useState<number>(Math.ceil(totalCount / pagination.per_page));
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>(accounts);
  

  const { getAccess } = useAccess();
  const [search, setSearch] = useState<string | null>(null);
  const breadcrumbItems = [{ label: "Accounts", onClick: () => router.get("/accounts"), }];

  const handlePageChange = (page: number) => { 
    setPagination(prev => ({ ...prev, page }));
  };

  const handleSearch = (search: string) => {
    setSearch(search);
    const filteredData =accounts.filter((account) =>
      !search
        ? true
        : account.name.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredAccounts(filteredData)
    setTotalCount(filteredData.length);
    setTotalPages(Math.ceil(filteredData.length / pagination.per_page));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <>
      <Container>
        <PageHeader title="Accounts" breadcrumbItems={breadcrumbItems}>
          <div className="space-x-2">
          {getAccess(["create_account"]) ? (
            <Link href="/accounts/new">
              <Button className="inline-flex items-center gap-2 text-black">
                <Plus size={16} /> <span>Add Account</span>
              </Button>
            </Link>
          ) : (
            <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed">
              <Button className="inline-flex items-center gap-2 text-gray-500" disabled>
                <Plus size={16} /> <span>Add Account</span>
              </Button>
            </span>
          )}
          </div>
        </PageHeader>
        <div className="flex items-center justify-between pt-6 pb-4">
          <SearchBox onSearch={handleSearch} />
          <div className="flex gap-1">
          {getAccess(["export_accounts"]) ? (
            <Link href="/accounts/export">
              <Button
                variant="outline"
                className="inline-flex items-center gap-2 border-border"
              >
                <ArrowUpDown size={16} /> <span>Export</span>
              </Button>
            </Link>
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed">
              <Button
                variant="outline"
                className="inline-flex items-center gap-2 border-border"
                disabled
              >
                <ArrowUpDown size={16} /> <span>Export</span>
              </Button>
            </span>
          )}
          </div>
        </div>
        <div className="border-b border-border" />
        <DataTable
          columns={columns}
          data={filteredAccounts}
          rowUrl={(row) => `${location.pathname}/${row.id}`}
          currentPage={pagination.page}
          pageSize={pagination.per_page}
        />
        <Pagination
          currentPage={pagination.page}
          totalPages={totalPages}
          totalCount={totalCount}
          perPage={pagination.per_page}
          onPageChange={handlePageChange}
        />
      </Container>
      

    </>
  );
};

export default Index;
